import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  ShuffleIcon,
} from '@phosphor-icons/react'
import { ActionIcon, Box, Center, Group, Text, UnstyledButton } from '@mantine/core'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import type { BoggleGameState, BoggleSkillMode } from '~/boggle/engine/standard/types'
import { BOGGLE_RUN_SEED_BOTTOM_INSET_PX } from '~/boggle/ui/chrome/AppLayout'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import { useBoggleIntervalPrepActive } from '~/boggle/ui/state/Store'
import { useBoggleResetCooldownActive } from '~/boggle/ui/run/BoggleResetCooldown'
import skillUi from './SkillControls.module.css'

function formatSkillCount(count: number, unlimited: boolean) {
  return unlimited ? '∞' : String(count)
}

type SkillCardModel = {
  count: number
  label: string
  labelContent: ReactNode
  mode: BoggleSkillMode
  unlimited: boolean
}

export type BoggleSkillControlModel = ReturnType<typeof useBoggleSkillControlModel>

/** Single subscriber for reset cooldown; call once under the active run controls tree. */
export function useBoggleSkillControlModel(state: BoggleGameState, store: BoggleStore) {
  const resetCooldownActive = useBoggleResetCooldownActive(store, state)
  const inIntervalCountdown = useBoggleIntervalPrepActive(
    store,
    state.intervalPlayBeginsAtMs,
  )
  const skillButtons: SkillCardModel[] = useMemo(
    () => [
      {
        count: state.resetsRemaining,
        label: 'Reset',
        labelContent: <IconGlyph icon={ArrowClockwiseIcon} size={28} />,
        mode: 'reset',
        unlimited: state.playgroundSettings.skills.reset.uses === null,
      },
      {
        count: state.shufflesRemaining,
        label: 'Shuffle',
        labelContent: <IconGlyph icon={ShuffleIcon} size={28} />,
        mode: 'shuffle',
        unlimited: state.playgroundSettings.skills.shuffle.uses === null,
      },
      {
        count: state.zapsRemaining,
        label: 'Zap',
        labelContent: 'Zap',
        mode: 'zap',
        unlimited: state.playgroundSettings.skills.zap.uses === null,
      },
      {
        count: state.swapsRemaining,
        label: 'Swap',
        labelContent: 'Swap',
        mode: 'swap',
        unlimited: state.playgroundSettings.skills.swap.uses === null,
      },
      {
        count: state.copiesRemaining,
        label: 'Copy',
        labelContent: 'Copy',
        mode: 'copy',
        unlimited: state.playgroundSettings.skills.copy.uses === null,
      },
    ],
    [
      state.copiesRemaining,
      state.playgroundSettings.skills.copy.uses,
      state.playgroundSettings.skills.reset.uses,
      state.playgroundSettings.skills.shuffle.uses,
      state.playgroundSettings.skills.swap.uses,
      state.playgroundSettings.skills.zap.uses,
      state.resetsRemaining,
      state.shufflesRemaining,
      state.swapsRemaining,
      state.zapsRemaining,
    ],
  )
  const visibleSkillButtons = useMemo(
    () =>
      skillButtons.filter(
        skill =>
          state.playgroundSettings.skills[skill.mode].enabled &&
          !(skill.mode === 'reset' && inIntervalCountdown),
      ),
    [inIntervalCountdown, skillButtons, state.playgroundSettings.skills],
  )
  const dockSkillButtons = useMemo(
    () => visibleSkillButtons.filter(skill => skill.mode !== 'reset'),
    [visibleSkillButtons],
  )
  const floatingResetSkill = useMemo(
    () => visibleSkillButtons.find(skill => skill.mode === 'reset'),
    [visibleSkillButtons],
  )
  const showRotateControls = state.phase === 'playing' && state.rotationEnabled

  return useMemo(
    () => ({
      dockSkillButtons,
      floatingResetSkill,
      resetCooldownActive,
      showRotateControls,
    }),
    [dockSkillButtons, floatingResetSkill, resetCooldownActive, showRotateControls],
  )
}

/** Reset anchored to viewport bottom-right; other skill actions stay under the Selection panel. */
export function FloatingResetSkillButton({
  isPaused,
  model,
  runUiSuspended,
  state,
  store,
}: {
  isPaused: boolean
  model: BoggleSkillControlModel
  runUiSuspended: boolean
  state: BoggleGameState
  store: BoggleStore
}) {
  if (
    runUiSuspended ||
    state.phase !== 'playing' ||
    isPaused ||
    model.floatingResetSkill === undefined
  ) {
    return null
  }

  const skill = model.floatingResetSkill
  const dimmed = model.resetCooldownActive
  const countDisabled = skill.count === 0 && state.activeSkill !== skill.mode

  return (
    <Box bottom={16} pos="fixed" right={16} style={{ zIndex: 30 }}>
      <UnstyledButton
        aria-label={skill.label}
        aria-pressed={state.activeSkill === skill.mode}
        className={skillUi.skill}
        bdrs={18}
        data-active={state.activeSkill === skill.mode || undefined}
        data-kind={skill.mode}
        disabled={countDisabled || dimmed}
        h={60}
        miw={100}
        onClick={() => {
          store.toggleSkill(skill.mode)
        }}
        opacity={dimmed ? 0.4 : undefined}
        px={10}
        py={12}
        ta="center"
      >
        {typeof skill.labelContent === 'string' ? (
          <Text c="#caa46a" fz={16} lts="0.12em" mih={24} tt="uppercase">
            {skill.labelContent}
          </Text>
        ) : (
          <Center aria-hidden="true" c="#caa46a" mih={24}>
            {skill.labelContent}
          </Center>
        )}
        {!skill.unlimited ? (
          <Text fz={23} fw={700} mt={6}>
            {formatSkillCount(skill.count, skill.unlimited)}
          </Text>
        ) : null}
      </UnstyledButton>
    </Box>
  )
}

export function SkillControls({
  model,
  state,
  store,
}: {
  model: BoggleSkillControlModel
  state: BoggleGameState
  store: BoggleStore
}) {
  const showSkillDockSection =
    model.showRotateControls || model.dockSkillButtons.length > 0

  if (!showSkillDockSection) {
    return null
  }

  return (
    <Group align="center" justify="center" mt={8}>
      {model.showRotateControls ? (
        <ActionIcon
          bdrs={25}
          className={skillUi.rotate}
          h={50}
          onClick={() => {
            store.rotateBoard('left')
          }}
          variant="default"
          w={50}
        >
          <IconGlyph icon={ArrowCounterClockwiseIcon} size={20} />
        </ActionIcon>
      ) : null}

      <Group gap={16} justify="center">
        {model.dockSkillButtons.map(skill => {
          const countDisabled = skill.count === 0 && state.activeSkill !== skill.mode
          return (
            <UnstyledButton
              aria-label={skill.label}
              aria-pressed={state.activeSkill === skill.mode}
              className={skillUi.skill}
              bdrs={18}
              data-active={state.activeSkill === skill.mode || undefined}
              data-kind={skill.mode}
              disabled={countDisabled}
              key={skill.mode}
              h={60}
              miw={140}
              onClick={() => {
                store.toggleSkill(skill.mode)
              }}
              px={10}
              py={12}
              ta="center"
            >
              {typeof skill.labelContent === 'string' ? (
                <Text c="#caa46a" fz={16} lts="0.12em" mih={24} tt="uppercase">
                  {skill.labelContent}
                </Text>
              ) : (
                <Center aria-hidden="true" c="#caa46a" mih={24}>
                  {skill.labelContent}
                </Center>
              )}
              {!skill.unlimited ? (
                <Text fz={23} fw={700} mt={6}>
                  {formatSkillCount(skill.count, skill.unlimited)}
                </Text>
              ) : null}
            </UnstyledButton>
          )
        })}
      </Group>

      {model.showRotateControls ? (
        <ActionIcon
          bdrs={25}
          className={skillUi.rotate}
          h={50}
          onClick={() => {
            store.rotateBoard('right')
          }}
          variant="default"
          w={50}
        >
          <IconGlyph icon={ArrowClockwiseIcon} size={28} />
        </ActionIcon>
      ) : null}
    </Group>
  )
}
